import { removeDuplicateItems } from '../../../array';
import { Municipality, locationTypes } from '../../../locations';
import PropertySearchFilter from '../../../property/filters';
import { getSourceFeatures, whenFeaturesAvailable } from '../../features';
import { removeApproxLocationCircle, removeLayersForSource } from '../../layers';
import PropertyResponse from '../../models/property-response';
import { removeRadiusFromMap } from '../../radius';
import { defaultPropertyFilters, newFeatureFilter, newPropertyFilter } from '../../request-filters';
import MapSearch from '../../search-map';

type SearchOptions = {
    triggerStateChange?: boolean,
}

class FindYourPlace {
    _mapSearch: MapSearch;

    constructor( mapSearch: MapSearch, options = {} ) {
        this._mapSearch = mapSearch;
    }

    async search( metaOptions: SearchOptions ): Promise<void> {
        locationTypes.forEach( ( locationType ) => {
            if ( locationType === Municipality ) {
                return;
            }

            if ( this._mapSearch.map.getLayer( locationType + ':fill' ) ) {
                this._mapSearch.map.removeLayer( locationType + ':fill' );
            }

            if ( this._mapSearch.map.getLayer( locationType + ':outline' ) ) {
                this._mapSearch.map.removeLayer( locationType + ':outline' );
            }
        } );

        this._mapSearch.currentLocation = null;

        removeRadiusFromMap( this._mapSearch.map );

        removeApproxLocationCircle( this._mapSearch.map );

        this._mapSearch.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:hide-remove-search-area', { bubbles: true } ) );

        this._mapSearch.root.dispatchEvent( new CustomEvent( 'twc:mapsearch:remove-area', {
            bubbles: true,
            detail: {
                search: false,
            },
        } ) );

        const previousSearch = PropertySearchFilter.fromURLSearchParams( new URLSearchParams( this._mapSearch.previousSearchState ) );

        if ( !previousSearch.anyLocationFilters() && this._mapSearch.map.getSource( Municipality ) ) {
            removeLayersForSource( this._mapSearch.map, Municipality );
            this._mapSearch.map.removeSource( Municipality );
        }

        const existingMunicipalities = removeDuplicateItems( getSourceFeatures( this._mapSearch.map, Municipality ).map( ( hit ) => hit.id ) );

        const results = await this._mapSearch.update( {
            fetchFeatures: false,
            fetchProperties: true,
            propertySearchURL: this._mapSearch.propertySearchURL,
            propertySearchParams: newPropertyFilter( {
                onlyProperties: false,
                take: 0,
                existingLocations: existingMunicipalities,
            }, this._mapSearch.baseForm as HTMLFormElement ),
            expectProperties: false,
        }, {
            tasks: [],
            asyncTasks: [],
            focusMapToSearch: false,
            initialSearch: false,
            addArea: false,
            triggerStateChange: false,
            recalculateBboxAfterAnimation: false,
        } );

        const propertyCallResponse = results.find( ( r ) => r instanceof PropertyResponse );

        if ( !propertyCallResponse ) {
            throw new Error( 'failed to find PropertyResponse instance' );
        }

        const response = propertyCallResponse.response;

        const matchedLocations = response.locationFilterMatches as Array<number>;

        await this._mapSearch.update( {
            fetchFeatures: true,
            featureSearchParams: newFeatureFilter( {
                locationIds: matchedLocations,
            } ),
            featureSearchURL: new URL( `/${Municipality}`, this._mapSearch.apiURL ),
            fetchProperties: false,
            expectProperties: false,
        }, {
            focusMapToSearch: true,
            appendFeaturesIfLayerExists: false,
            triggerStateChange: false,
            fitBounds: {
                options: {
                    padding: {
                        top: 20,
                        right: 20,
                        bottom: 20,
                        left: 20,
                    },
                },
            },
        } );

        whenFeaturesAvailable( this._mapSearch.map, {
            source: Municipality,
        }, this._mapSearch.abortController.signal ).then( ( features ) => {
            for ( let i = 0; i < features.length; i++ ) {
                this._mapSearch.map.setFeatureState(
                    {
                        source: Municipality,
                        id: features[ i ].id,
                    },
                    {
                        active: true,
                    } );
            }
        } )
            .catch( ( err ) => {
                if ( err.name === 'AbortError' ) {
                    return;
                }
            } );

        const propertyParams = newPropertyFilter( {
            onlyProperties: false,
            take: defaultPropertyFilters.take,
        }, this._mapSearch.baseForm as HTMLFormElement );

        await this._mapSearch.update( {
            fetchFeatures: false,
            fetchProperties: true,
            propertySearchURL: this._mapSearch.propertySearchURL,
            propertySearchParams: propertyParams,
            expectProperties: true,
        }, metaOptions );

        this._mapSearch.previousSearchState = propertyParams.toString();

        this._mapSearch.setLoading( false );
    }
}

export default FindYourPlace;
